import { ArrowRightMini } from '@medusajs/icons';
import { Fragment, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { NewsItemDto, NewsResponseDto } from '@marxbiotech/shared-types';

import { ErrorBoundary } from '../../../../error-boundary';
import BrandTitle from '@modules/common/components/brand-title';
import Button from '@modules/common/components/button';
import { Divider } from '@modules/common/components/divider';
import Text from '@modules/common/components/text';
import Modal from '@modules/common/components/modal';
import { RichContent } from '@modules/common/components/rich-content';

type LatestNewsItemProps = {
  openModal: (item: NewsItemDto) => void;
  item: NewsItemDto;
};

const LatestNewsItem = ({ openModal, item }: LatestNewsItemProps) => {
  return (
    <div className="flex items-center justify-between">
      <div>
        <Text variant="body-bold" as="h3" className="text-black-20">{item.title}</Text>
        <Text variant="sm-regular" as="p" className="text-grey-10">
          {new Date(item.createdAt).toDateString()}
        </Text>
      </div>
      <button
        onClick={() => openModal(item)}
        aria-label={`查看 ${item.title} 詳情`}
        className="hover:text-primary-30 cursor-pointer transition-all ease-in hover:translate-x-1"
      >
        <ArrowRightMini />
      </button>
    </div>
  );
};

const LatestNews = ({
  type = 'full',
  news = {
    docs: [],
    nextPage: null,
  },
}: {
  type?: 'full' | 'compact';
  news: NewsResponseDto;
}) => {
  const { countryCode } = useParams<{ countryCode: string }>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openItem, setOpenItem] = useState<NewsItemDto | null>(null);
  const openModal = (item: NewsItemDto) => {
    setOpenItem(item);
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
    setOpenItem(null);
  };
  return (
    <div className="mx-auto w-full max-w-[900px] px-4 md:px-8">
      <div className="flex justify-center">
        <BrandTitle title="最新消息" />
      </div>
      <div className="mt-4 rounded-xl border p-8 md:mt-12">
        <div>
          {news.docs.map((item, idx) => (
            <Fragment key={item.id || idx}>
              <LatestNewsItem openModal={openModal} item={item} />
              <Divider className="my-4 last:hidden" />
            </Fragment>
          ))}
        </div>
        {type === 'compact' && news.nextPage !== null && (
          <div className="mt-4 text-center">
            <Button as="link" href={`/${countryCode}/latest`} variant="outlined">
              看更多
            </Button>
          </div>
        )}
      </div>
      <Modal
        isOpen={isModalOpen}
        close={closeModal}
        panelClassName="bg-white-10 max-w-[750px]"
      >
        <Text variant="title-bold" as="h3" className="text-black-40">{openItem?.title}</Text>
        <ErrorBoundary
          fallback={<p className="text-red-500">內容載入失敗</p>}
        >
          <RichContent nodes={openItem?.content ?? []} />
        </ErrorBoundary>
      </Modal>
    </div>
  );
};

export default LatestNews;
